import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { VendorAssignmentCard } from './VendorAssignmentCard';
import { CategorySelector } from './CategorySelector';
import { Service } from '@/types/service';
import { ImageCropper } from '@/components/ui/image-cropper';

interface ServiceFormFieldsProps {
  initialData: Partial<Service>;
  users: any[];
  regions: any[];
  isAdmin: boolean;
  isAuthorized: boolean;
  isAddMode: boolean;
  loading: boolean;
  onFormChange: (data: Partial<Service>) => void;
  onImageChange?: (file: File | null) => void;
  adminOnly?: boolean;
}

export const ServiceFormFields: React.FC<ServiceFormFieldsProps> = ({
  initialData,
  users,
  regions,
  isAdmin,
  isAuthorized,
  isAddMode,
  loading,
  onFormChange,
  onImageChange,
  adminOnly = false
}) => {
  const [formData, setFormData] = useState<Partial<Service>>(initialData);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');

  // Update form data when initialData changes
  useEffect(() => {
    console.log('ServiceFormFields: initialData changed:', initialData);
    setFormData(initialData);
  }, [initialData]);

  // Notify parent of form changes
  useEffect(() => {
    onFormChange(formData);
  }, [formData, onFormChange]);

  const handleInputChange = (field: keyof Service, value: any) => {
    console.log('ServiceFormFields: Field change:', field, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubcategoryChange = (subcategoryId: number | null) => {
    console.log('ServiceFormFields: Subcategory change:', subcategoryId);
    handleInputChange('subcategory', subcategoryId);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create preview URL for cropping
      const previewUrl = URL.createObjectURL(file);
      setImagePreviewUrl(previewUrl);
      setPendingImageFile(file);
      setShowImageCropper(true);
    }
  };

  const handleCropComplete = (croppedImageBlob: Blob) => {
    // Convert blob to file
    const croppedFile = new File([croppedImageBlob], pendingImageFile?.name || 'cropped-image.jpg', {
      type: 'image/jpeg'
    });
    
    if (onImageChange) {
      onImageChange(croppedFile);
    }
    
    // Clean up
    setShowImageCropper(false);
    setPendingImageFile(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl('');
    }
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setPendingImageFile(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl('');
    }
  };

  const handleAssignmentChange = () => {
    // Trigger a refresh to update the vendor assignment display
    setRefreshKey(prev => prev + 1);
  };

  // If adminOnly is true, only show the admin settings card
  if (adminOnly) {
    return (
      <div className="space-y-6">
        {/* Admin Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Admin Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="region">Region</Label>
              <Select 
                value={formData.region || 'nairobi'} 
                onValueChange={(value) => handleInputChange('region', value)}
                disabled={!isAuthorized || loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region.code} value={region.code}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <CategorySelector
              subcategoryId={formData.subcategory || null}
              onSubcategoryChange={handleSubcategoryChange}
              disabled={!isAuthorized || loading}
            />

            <div>
              <Label htmlFor="owner_id">Vendor Owner</Label>
              <Select
                value={formData.owner_id || 'unassigned'}
                onValueChange={(value) => handleInputChange('owner_id', value === 'unassigned' ? null : value)}
                disabled={!isAuthorized || loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="unassigned" value="unassigned">
                    Unassigned
                  </SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="published"
                checked={formData.published || false}
                onCheckedChange={(checked) => handleInputChange('published', checked)}
                disabled={!isAuthorized || loading}
              />
              <Label htmlFor="published">Published</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="calendar"
                checked={formData.calendar || false}
                onCheckedChange={(checked) => handleInputChange('calendar', checked)}
                disabled={!isAuthorized || loading}
              />
              <Label htmlFor="calendar">Enable Calendar Bookings</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="featured"
                checked={formData.featured || false}
                onCheckedChange={(checked) => handleInputChange('featured', checked)}
                disabled={!isAuthorized || loading}
              />
              <Label htmlFor="featured">Featured Service</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="vendor_can_edit"
                checked={formData.vendor_can_edit || false}
                onCheckedChange={(checked) => handleInputChange('vendor_can_edit', checked)}
                disabled={!isAuthorized || loading}
              />
              <Label htmlFor="vendor_can_edit">Vendor Can Edit</Label>
            </div>

            <div>
              <Label htmlFor="display_order">Display Order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order === null ? '' : formData.display_order}
                onChange={(e) => handleInputChange('display_order', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                disabled={!isAuthorized || loading}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="test_only"
                checked={formData.test_only || false}
                onCheckedChange={(checked) => handleInputChange('test_only', checked)}
                disabled={!isAuthorized || loading}
              />
              <Label htmlFor="test_only">Test Only</Label>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showImageCropper && imagePreviewUrl) {
    return (
      <div className="space-y-6">
        <ImageCropper
          src={imagePreviewUrl}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={4/3}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Image Upload Card - enhanced with cropping info */}
      <Card>
        <CardHeader>
          <CardTitle>Service Image</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="image">Upload Image</Label>
            <p className="text-sm text-gray-500 mb-2">
              Upload an image and we'll help you crop it perfectly for service cards. 
              Recommended: Clear photos showing the full subject without important details at edges.
            </p>
            <Input
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={!isAuthorized || loading}
            />
            {formData.image_url && (
              <div className="mt-2">
                <img 
                  src={formData.image_url} 
                  alt="Service" 
                  className="w-32 h-32 object-cover rounded"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Basic Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="name">Service Name *</Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={!isAuthorized || loading}
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <RichTextEditor
              value={formData.description || ''}
              onChange={(value) => handleInputChange('description', value)}
              placeholder="Enter service description..."
              disabled={!isAuthorized || loading}
            />
          </div>

          {/* Price, Units, and Additional Person Price in one row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price || ''}
                onChange={(e) => handleInputChange('price', e.target.value)}
                disabled={!isAuthorized || loading}
              />
            </div>

            <div>
              <Label htmlFor="unit">Units</Label>
              <Select
                value={formData.unit || 'Hour'}
                onValueChange={(value) => handleInputChange('unit', value)}
                disabled={!isAuthorized || loading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hour">Hour</SelectItem>
                  <SelectItem value="Day">Day</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                  <SelectItem value="Person">Person</SelectItem>
                  <SelectItem value="Item">Item</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="additional_person">Price per Additional Person</Label>
              <Input
                id="additional_person"
                type="number"
                step="0.01"
                value={formData.additional_person || ''}
                onChange={(e) => handleInputChange('additional_person', e.target.value)}
                disabled={!isAuthorized || loading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              disabled={!isAuthorized || loading}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={formData.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              disabled={!isAuthorized || loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              This phone number will be used for vendor notifications when a vendor is assigned.
            </p>
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!isAuthorized || loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Admin Settings Card */}
      {isAdmin && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Admin Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="region">Region</Label>
                <Select 
                  value={formData.region || 'nairobi'} 
                  onValueChange={(value) => handleInputChange('region', value)}
                  disabled={!isAuthorized || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.code} value={region.code}>
                        {region.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <CategorySelector
                subcategoryId={formData.subcategory || null}
                onSubcategoryChange={handleSubcategoryChange}
                disabled={!isAuthorized || loading}
              />

              <div>
                <Label htmlFor="owner_id">Vendor Owner</Label>
                <Select
                  value={formData.owner_id || 'unassigned'}
                  onValueChange={(value) => handleInputChange('owner_id', value === 'unassigned' ? null : value)}
                  disabled={!isAuthorized || loading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem key="unassigned" value="unassigned">
                      Unassigned
                    </SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.display_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={formData.published || false}
                  onCheckedChange={(checked) => handleInputChange('published', checked)}
                  disabled={!isAuthorized || loading}
                />
                <Label htmlFor="published">Published</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="calendar"
                  checked={formData.calendar || false}
                  onCheckedChange={(checked) => handleInputChange('calendar', checked)}
                  disabled={!isAuthorized || loading}
                />
                <Label htmlFor="calendar">Enable Calendar Bookings</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="featured"
                  checked={formData.featured || false}
                  onCheckedChange={(checked) => handleInputChange('featured', checked)}
                  disabled={!isAuthorized || loading}
                />
                <Label htmlFor="featured">Featured Service</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="vendor_can_edit"
                  checked={formData.vendor_can_edit || false}
                  onCheckedChange={(checked) => handleInputChange('vendor_can_edit', checked)}
                  disabled={!isAuthorized || loading}
                />
                <Label htmlFor="vendor_can_edit">Vendor Can Edit</Label>
              </div>

              <div>
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order === null ? '' : formData.display_order}
                  onChange={(e) => handleInputChange('display_order', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  disabled={!isAuthorized || loading}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="test_only"
                  checked={formData.test_only || false}
                  onCheckedChange={(checked) => handleInputChange('test_only', checked)}
                  disabled={!isAuthorized || loading}
                />
                <Label htmlFor="test_only">Test Only</Label>
              </div>
            </CardContent>
          </Card>

          {/* Vendor Assignment Card - only show for existing services */}
          {!isAddMode && formData.id && (
            <VendorAssignmentCard
              key={refreshKey}
              service={formData}
              availableUsers={users}
              onAssignmentChange={handleAssignmentChange}
            />
          )}
        </>
      )}
    </div>
  );
};
